import React, { useEffect, useState } from 'react'
import { Card, Form, Button, Row, Col, DatePicker, Spin, Table, Select } from 'antd'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import BaseListing from 'src/BaseListings'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import WidgetProjectSelector from 'src/widgets/Selectors/WidgetProjectSelector'
import WidgetCustomerSelector from 'src/widgets/Selectors/WidgetCustomerSelector'
import { wtFormatRoundedValue, wtRoundValue } from 'src/helpers/commons/utils'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import { SALE_ORDER_INVOICES_DATE_NAMES, SALE_ORDER_INVOICES_DATE_TYPE_DROPDOWN_OPTIONS, SALE_ORDER_INVOICES_FBR_STATUS_DROPDOWN_OPTIONS, SALE_ORDER_INVOICES_STATUS_DROPDOWN_OPTIONS, SALE_ORDER_INVOICES_STATUS_NAME } from 'src/models/sale-order-invoice'
import WidgetDepartmentSelector from 'src/widgets/Selectors/WidgetDepartmentSelector'
import { DEPARTMENT_TYPE_VIEW_SALE_ORDER } from 'src/models/department-permission'
import { CFormInput } from '@coreui/react'
import dayjs from 'dayjs'
import { Table as AntTable } from 'antd'
import { useMasterDateTimeFormats } from 'src/helpers/commons/masterDateFormat'

const CLIENT_TYPE_FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Government Client', value: 'government' },
  { label: 'Non Government Client', value: 'non_government' },
]

const ITEM_LEVEL_OPTIONS = [
  { label: 'No', value: 0 },
  { label: 'Yes', value: 1 },
]

const SCRAP_SALE_FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
]

const FILTER_LABEL_STYLE = {
  display: 'block',
  marginBottom: 4,
  fontSize: 12,
  lineHeight: 1.2,
}

const FILTER_CONTROL_STYLE = {
  width: '100%',
  height: 32,
}

const COMPACT_FILTER_CONTROL_STYLE = {
  width: '100%',
  height: 28,
}

const INVOICE_LEVEL_TOTAL_KEYS = [
  'invoice_quantity',
  'total_amount_after_discount',
  'total_tax',
  'total_including_taxes',
  'total_additional_tax',
  'total_including_taxes_and_further_tax',
  'income_tax_head_amount',
  'grand_total_after_income_tax',
  'total_cost',
]

const ITEM_LEVEL_TOTAL_KEYS = [
  'invoice_quantity',
  'total_price',
  'discount_amount',
  'line_total_after_discount',
  'tax_amount',
  'total_including_taxes',
  'further_tax',
  'total_including_taxes_and_further_tax',
  'income_tax_head_amount',
  'grand_total_after_income_tax',
  'total_cost',
]

const getNumericAmount = (value) => Number(value) || 0

const getInvoiceTotalAfterDiscount = (row) => {
  const totalItemAmount = getNumericAmount(row?.total_item_amount)
  const totalDiscount = getNumericAmount(row?.total_discount)

  if (totalItemAmount || totalDiscount) {
    return totalItemAmount - totalDiscount
  }

  return getNumericAmount(row?.total_amount_after_discount)
}

const getItemLineTotalAfterDiscount = (row) => {
  const invoiceTotal = getNumericAmount(row?.invoice_total)

  if (invoiceTotal) {
    return invoiceTotal
  }

  return getNumericAmount(row?.line_total_after_discount)
}

const getRowTotalValue = (row, key, isItemLevel) => {
  if (key === 'total_amount_after_discount' && !isItemLevel) {
    return getInvoiceTotalAfterDiscount(row)
  }

  if (key === 'line_total_after_discount' && isItemLevel) {
    return getItemLineTotalAfterDiscount(row)
  }

  return getNumericAmount(row?.[key])
}

const SaleBookReport = () => {
  const Base = new BaseListing('sale_book_report')
  const { formatDate, formatDateTime } = useMasterDateTimeFormats()
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()
  const [branchId, setBranchId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const branches = wtGetFromLocalStorage('branches')
  const [customers, setCustomers] = useState([])
  const [projects, setProjects] = useState([])
  const [revenueAccounts, setRevenueAccounts] = useState([])
  const [ProjectId, setProjectId] = useState(null)
  const [ProjectName, setProjectName] = useState('')
  const [dateRange, setDateRange] = useState([])
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [PartyName, setPartyName] = useState('')
  const [PartyId, setPartyId] = useState(null)
  const [statusId, setStatusId] = useState(0)
  const [dateType, setDateType] = useState(0)
  const [searchMore, setSearchMore] = useState(false)
  const [saleBookReportData, setSaleBookReportData] = useState([])
  const [fbrStatus, setFbrStatus] = useState(null)

  const [departments, setDepartments] = useState([])
  const [departmentId, setDepartmentId] = useState(null)
  const [departmentName, setDepartmentName] = useState('')

  const [fbrInvoiceNo, setFbrInvoiceNo] = useState('')
  const [governmentClientType, setGovernmentClientType] = useState('all')
  const [RevenueAccountId, setRevenueAccountId] = useState(0)
  const [RevenueAccountName, setRevenueAccountName] = useState('')
  const [itemLevel, setItemLevel] = useState(0)
  const [scrapSaleType, setScrapSaleType] = useState('all')

  const [filtersLoaded, setFiltersLoaded] = useState(false)

  const reloadParties = () => Base.dropdownData('customers', {}, [setCustomers], 1)

  const changeValues = (key, val) => {
    setSaleBookReportData([])
    if (key === 'branch_id') {
      setBranchId(val.value)
      setBranchName(val.label)

      // reset department
      setDepartmentId(null)
      setDepartmentName('')
      setDepartments([])

      // fetch departments based on branch
      if (val?.value) {
        Base.dropdownData(
          'user_departments',
          { __branch_id: val.value, type: DEPARTMENT_TYPE_VIEW_SALE_ORDER },
          [setDepartments],
          1
        )

        Base.dropdownData(
          'sale_book_revenue_accounts',
          { branch_id: val.value },
          [setRevenueAccounts],
          1
        )
      } else {
        Base.dropdownData(
          'sale_book_revenue_accounts',
          {},
          [setRevenueAccounts],
          1
        )
      }
    }
    if (key === 'department_id') {

      setDepartmentId(val.value)
      setDepartmentName(val.label)
    }
    if (key === 'project_id') {
      setProjectId(val)
      const selectedProject = projects.find((p) => p.value === val)
      setProjectName(selectedProject ? selectedProject.title : '')
    }
    if (key === 'date_range') {
      setDateRange(val)
    }
    if (key === 'customer_id') {
      setPartyId(val)
      if (val === null) {
        setPartyName('')
      }
    }
    if (key === 'status') {
      if (val === null) {
        setStatusId(0)
      }
      setStatusId(val?.value)
    }
    if (key === 'date_type') {
      if (val === null) {
        setDateType(0)
      }
      setDateType(val?.value)
    }

    if (key === 'fbr_status') {
      if (key === 'fbr_status') {
        setFbrStatus(val ?? null)
      }
    }

    if (key === 'fbr_invoice_no') {
      setFbrInvoiceNo(val)
    }

    if (key === 'government_client_type') {
      setGovernmentClientType(val)
    }

    if (key === 'revenue_account_id') {
      setRevenueAccountId(val || 0)
      if (!val) {
        setRevenueAccountName('')
      }
    }

    if (key === 'item_level') {
      setItemLevel(val ?? 0)
    }

    if (key === 'scrap_sale_type') {
      setScrapSaleType(val ?? 'all')
    }
  }

  useEffect(() => {
    Base.dropdownData(
      'customers,projects,sale_book_revenue_accounts',
      {},
      [setCustomers, setProjects, setRevenueAccounts],
      1,
    )
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if ([...params.keys()].length > 0) {
      // SET STATES
      setBranchId(Number(params.get('branch_id')) || 0);
      setBranchName(params.get('branch_name') || '');


      const partyIdParam = params.get('party_id');
      setPartyId(partyIdParam === 'null' ? null : +partyIdParam);
      setPartyName(params.get('party_name') || '');

      const projectIdParam = params.get('project_id');
      setProjectId(projectIdParam === 'null' ? null : projectIdParam);
      setProjectName(params.get('project_name') || '');

      setStatusId(Number(params.get('status')) || 0);
      setDateType(Number(params.get('date_type')) || 0);

      setFbrStatus(+params.get('fbr_status'));
      setDepartmentId(params.get('department_id'));
      setDepartmentName(params.get('department_name') || '');
      setGovernmentClientType(params.get('government_client_type') || 'all');
      setRevenueAccountId(Number(params.get('revenue_account_id')) || 0);
      setRevenueAccountName(params.get('revenue_account_name') || '');
      setItemLevel(Number(params.get('item_level')) || 0);
      setScrapSaleType(params.get('scrap_sale_type') || 'all');

      // ✅ DATE FIX
      const date = params.get('date');
      if (date) {
        const start = dayjs(date).startOf('month');
        const end = dayjs(date).endOf('month');

        setDateRange([start, end]);
      }

      // ✅ mark ready
      setFiltersLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!filtersLoaded) return;

    getReportData();
  }, [filtersLoaded]);

  const getReportData = () => {
    if (dateRange.length < 2) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }
    const fromDate = dateRange[0]
    const toDate = dateRange[1]
    setIsLoading(true)
    wtApiCall(
      'reports/sale_book_report',
      {
        branch_id: branchId,
        from_date: fromDate.format('YYYY-MM-DD'),
        to_date: toDate.format('YYYY-MM-DD'),
        party_id: PartyId,
        project_id: ProjectId,
        status: statusId,
        date_type: dateType,
        fbr_status: fbrStatus,
        department_id: departmentId,
        fbr_inv_no: fbrInvoiceNo,
        government_client_type: governmentClientType,
        revenue_account_id: RevenueAccountId,
        item_level: itemLevel,
        scrap_sale_type: scrapSaleType
      },
      'get',
      (response) => {
        setSaleBookReportData(response.data)
        setIsLoading(false)
        setFiltersLoaded(true);
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
        setFiltersLoaded(true);
      },
    )
  }

  const exportToExcel = () => {
    setIsLoading(true)
    wtApiPostWithContentTypeHandling('sale_book_report/export', { data: saleBookReportData, item_level: itemLevel })
    setIsLoading(false)
  }

  const invoiceLevelColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      fixed: 'left',
      width: 60,
      ellipsis: true,
    },
    {
      title: 'Invoice Date',
      dataIndex: 'invoice_reference_date',
      key: 'invoice_reference_date',
      fixed: 'left',
      width: 50,
      ellipsis: true,
      render: (value) => formatDate(value),
    },
    {
      title: 'Branch',
      dataIndex: 'branch_name',
      key: 'branch_name',
      width: 70,
      ellipsis: true,
    },
    {
      title: 'Customer',
      dataIndex: 'party_name',
      key: 'party_name',
      width: 100,
      ellipsis: true,
    },
    {
      title: 'Project',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 60,
      ellipsis: true,
    },
    {
      title: 'Additional Details',
      dataIndex: 'additional_details',
      key: 'additional_details',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'NTN/CNIC No',
      dataIndex: 'ntn',
      key: 'ntn',
      width: 80,
      ellipsis: true,
    },
    {
      title: 'Invoice No',
      dataIndex: 'invoice_no',
      key: 'invoice_no',
      width: 40,
      ellipsis: true,
    },
    {
      title: 'DO ID',
      dataIndex: 'do_id',
      key: 'do_id',
      width: 60,
      ellipsis: true,
    },
    {
      title: 'D.O No.',
      dataIndex: 'delivery_no',
      key: 'delivery_no',
      width: 40,
      ellipsis: true,
    },
    {
      title: 'Sale Tax Invoice No',
      dataIndex: 'invoice_reference_no',
      key: 'invoice_reference_no',
      width: 70,
      ellipsis: true,
    },
    {
      title: 'FBR Status',
      dataIndex: 'fbr_status',
      key: 'fbr_status',
      width: 70,
      ellipsis: true,
    },
    {
      title: 'FBR Inv No',
      dataIndex: 'fbr_invoice_reference_no',
      key: 'fbr_invoice_reference_no',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Reported At',
      dataIndex: 'reported_at',
      key: 'reported_at',
      width: 100,
      ellipsis: true,
      render: (value) => formatDateTime(value),
    },
    {
      title: 'Reported By',
      dataIndex: 'reported_by',
      key: 'reported_by',
      width: 100,
      ellipsis: true,
    },
    {
      key: 'invoice_quantity',
      title: 'QTY',
      width: 40,
      render: (text, record) => wtRoundValue(record.invoice_quantity),
    },
    {
      title: 'Total After Discount',
      dataIndex: 'total_amount_after_discount',
      key: 'total_amount_after_discount',
      width: 90,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(getInvoiceTotalAfterDiscount(record), "", false, 2),
    },
    {
      title: 'Sales Tax Name',
      dataIndex: 'tax_name',
      key: 'tax_name',
      width: 90,
      ellipsis: true,
    },
    {
      title: 'Tax Rate',
      dataIndex: 'tax_rate',
      key: 'tax_rate',
      width: 80,
      ellipsis: true,
    },
    {
      title: 'Tax Amount',
      dataIndex: 'total_tax',
      key: 'total_tax',
      width: 60,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.total_tax, "", false, 2),
    },
    {
      title: 'Total Including Taxes',
      dataIndex: 'total_including_taxes',
      key: 'total_including_taxes',
      width: 80,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.total_including_taxes, "", false, 2),
    },
    {
      title: 'Further Tax Name',
      dataIndex: 'further_tax_name',
      key: 'further_tax_name',
      width: 90,
      ellipsis: true,
    },
    {
      title: 'Further Tax Rate',
      dataIndex: 'additional_tax_rate',
      key: 'additional_tax_rate',
      width: 70,
      ellipsis: true,
      render: (text, record) => `${wtFormatRoundedValue(record.additional_tax_rate, "", false, 2)}%`,
    },
    {
      title: 'Further Tax',
      dataIndex: 'total_additional_tax',
      key: 'total_additional_tax',
      width: 70,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.total_additional_tax, "", false, 2),
    },
    {
      title: 'Total Including Taxes and Further Tax',
      dataIndex: 'total_including_taxes_and_further_tax',
      key: 'total_including_taxes_and_further_tax',
      width: 100,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.total_including_taxes_and_further_tax, "", false, 2),
    },
    {
      title: 'Scrap Sale',
      dataIndex: 'scrap_sale_name',
      key: 'scrap_sale_name',
      width: 70,
      ellipsis: true,
    },
    {
      title: 'Income Tax Name',
      dataIndex: 'income_tax_name',
      key: 'income_tax_name',
      width: 90,
      ellipsis: true,
    },
    {
      title: 'Income Tax Rate',
      dataIndex: 'income_tax_head_rate',
      key: 'income_tax_head_rate',
      width: 70,
      ellipsis: true,
      render: (text, record) => `${wtFormatRoundedValue(record.income_tax_head_rate, "", false, 2)}%`,
    },
    {
      title: 'Income Tax',
      dataIndex: 'income_tax_head_amount',
      key: 'income_tax_head_amount',
      width: 80,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.income_tax_head_amount, "", false, 2),
    },
    {
      title: 'Grand Total Including Income',
      dataIndex: 'grand_total_after_income_tax',
      key: 'grand_total_after_income_tax',
      width: 100,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.grand_total_after_income_tax, "", false, 2),
    },
    {
      title: 'Unit Cost',
      dataIndex: 'unit_cost',
      key: 'unit_cost',
      width: 80,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.unit_cost, "", false, 2),
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost',
      key: 'total_cost',
      width: 80,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.total_cost, "", false, 2),
    },
    {
      title: 'Revenue Account',
      dataIndex: 'revenue_account_name',
      key: 'revenue_account_name',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Warehouse',
      dataIndex: 'warehouse_name',
      key: 'warehouse_name',
      width: 140,
      ellipsis: true,
    },

  ]

  const itemLevelColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      fixed: 'left',
      width: 60,
      ellipsis: true,
    },
    {
      title: 'Invoice Date',
      dataIndex: 'invoice_reference_date',
      key: 'invoice_reference_date',
      fixed: 'left',
      width: 90,
      ellipsis: true,
      render: (value) => formatDate(value),
    },
    {
      title: 'Branch',
      dataIndex: 'branch_name',
      key: 'branch_name',
      width: 100,
      ellipsis: true,
    },
    {
      title: 'Customer',
      dataIndex: 'party_name',
      key: 'party_name',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Project',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Additional Details',
      dataIndex: 'additional_details',
      key: 'additional_details',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'NTN/CNIC No',
      dataIndex: 'ntn',
      key: 'ntn',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Invoice No',
      dataIndex: 'invoice_no',
      key: 'invoice_no',
      width: 100,
      ellipsis: true,
    },
    {
      title: 'DO ID',
      dataIndex: 'do_id',
      key: 'do_id',
      width: 60,
      ellipsis: true,
    },
    {
      title: 'D.O No.',
      dataIndex: 'delivery_no',
      key: 'delivery_no',
      width: 100,
      ellipsis: true,
    },
    {
      title: 'Sale Tax Invoice No',
      dataIndex: 'invoice_reference_no',
      key: 'invoice_reference_no',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'FBR Status',
      dataIndex: 'fbr_status',
      key: 'fbr_status',
      width: 110,
      ellipsis: true,
    },
    {
      title: 'FBR Inv No',
      dataIndex: 'fbr_invoice_reference_no',
      key: 'fbr_invoice_reference_no',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Reported At',
      dataIndex: 'reported_at',
      key: 'reported_at',
      width: 130,
      ellipsis: true,
      render: (value) => formatDateTime(value),
    },
    {
      title: 'Reported By',
      dataIndex: 'reported_by',
      key: 'reported_by',
      width: 130,
      ellipsis: true,
    },
    {
      title: 'HS Code',
      dataIndex: 'hs_code',
      key: 'hs_code',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Product Description',
      dataIndex: 'hs_code_description',
      key: 'hs_code_description',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Item Code',
      dataIndex: 'item_code',
      key: 'item_code',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Item Name',
      dataIndex: 'item_name',
      key: 'item_name',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'UoM',
      dataIndex: 'item_unit_name',
      key: 'item_unit_name',
      width: 90,
      ellipsis: true,
    },
    {
      title: 'Item QTY',
      dataIndex: 'invoice_quantity',
      key: 'invoice_quantity',
      width: 90,
      ellipsis: true,
      render: (text, record) => wtRoundValue(record.invoice_quantity),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 100,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.unit_price, '', false, 2),
    },
    {
      title: 'Total Price',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 100,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.total_price, '', false, 2),
    },
    {
      title: 'Discount',
      dataIndex: 'discount_amount',
      key: 'discount_amount',
      width: 100,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.discount_amount, '', false, 2),
    },
    {
      title: 'Total After Discount',
      dataIndex: 'line_total_after_discount',
      key: 'line_total_after_discount',
      width: 130,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(getItemLineTotalAfterDiscount(record), '', false, 2),
    },
    {
      title: 'Sales Tax Name',
      dataIndex: 'tax_name',
      key: 'tax_name',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Tax Rate',
      dataIndex: 'tax_rate',
      key: 'tax_rate',
      width: 90,
      ellipsis: true,
      render: (text, record) => `${wtFormatRoundedValue(record.tax_rate, '', false, 2)}%`,
    },
    {
      title: 'Tax Amount',
      dataIndex: 'tax_amount',
      key: 'tax_amount',
      width: 100,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.tax_amount, '', false, 2),
    },
    {
      title: 'Total Including Taxes',
      dataIndex: 'total_including_taxes',
      key: 'total_including_taxes',
      width: 140,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.total_including_taxes, '', false, 2),
    },
    {
      title: 'Further Tax Name',
      dataIndex: 'further_tax_name',
      key: 'further_tax_name',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Further Tax Rate',
      dataIndex: 'additional_tax_rate',
      key: 'additional_tax_rate',
      width: 110,
      ellipsis: true,
      render: (text, record) => `${wtFormatRoundedValue(record.additional_tax_rate, '', false, 2)}%`,
    },
    {
      title: 'Further Tax',
      dataIndex: 'further_tax',
      key: 'further_tax',
      width: 100,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.further_tax, '', false, 2),
    },
    {
      title: 'Total Including Taxes and Further Tax',
      dataIndex: 'total_including_taxes_and_further_tax',
      key: 'total_including_taxes_and_further_tax',
      width: 170,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.total_including_taxes_and_further_tax, '', false, 2),
    },
    {
      title: 'Scrap Sale',
      dataIndex: 'scrap_sale_name',
      key: 'scrap_sale_name',
      width: 100,
      ellipsis: true,
    },
    {
      title: 'Income Tax Name',
      dataIndex: 'income_tax_name',
      key: 'income_tax_name',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Income Tax Rate',
      dataIndex: 'income_tax_head_rate',
      key: 'income_tax_head_rate',
      width: 110,
      ellipsis: true,
      render: (text, record) => `${wtFormatRoundedValue(record.income_tax_head_rate, '', false, 2)}%`,
    },
    {
      title: 'Income Tax',
      dataIndex: 'income_tax_head_amount',
      key: 'income_tax_head_amount',
      width: 100,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.income_tax_head_amount, '', false, 2),
    },
    {
      title: 'Grand Total Including Income',
      dataIndex: 'grand_total_after_income_tax',
      key: 'grand_total_after_income_tax',
      width: 160,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.grand_total_after_income_tax, '', false, 2),
    },
    {
      title: 'Unit Cost',
      dataIndex: 'unit_cost',
      key: 'unit_cost',
      width: 100,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.unit_cost, '', false, 2),
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost',
      key: 'total_cost',
      width: 100,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.total_cost, '', false, 2),
    },
    {
      title: 'Revenue Account',
      dataIndex: 'revenue_account_name',
      key: 'revenue_account_name',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'Cost Account',
      dataIndex: 'cost_account_name',
      key: 'cost_account_name',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'Inventory Account',
      dataIndex: 'inventory_account_name',
      key: 'inventory_account_name',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'Warehouse',
      dataIndex: 'warehouse_name',
      key: 'warehouse_name',
      width: 160,
      ellipsis: true,
    },
  ]

  const columns = (itemLevel === 1 ? itemLevelColumns : invoiceLevelColumns).map((column) => ({
    ...column,
    width: typeof column.width === 'number' ? Math.max(column.width, 100) : column.width,
    ellipsis: false,
    onCell: () => ({
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        verticalAlign: 'top',
      },
    }),
    onHeaderCell: () => ({
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        verticalAlign: 'top',
      },
    }),
  }))

  const isItemLevelView = itemLevel === 1
  const totalKeys = isItemLevelView ? ITEM_LEVEL_TOTAL_KEYS : INVOICE_LEVEL_TOTAL_KEYS
  const totals = totalKeys.reduce((acc, key) => {
    acc[key] = saleBookReportData.reduce(
      (sum, row) => sum + getRowTotalValue(row, key, isItemLevelView),
      0,
    )
    return acc
  }, {})

  const renderTableSummary = () => (
    <AntTable.Summary.Row>
      {columns.map((column, index) => {
        const isTotalColumn = totalKeys.includes(column.key)
        const rawValue = totals[column.key] || 0
        const content = index === 0
          ? 'Total'
          : isTotalColumn
            ? (column.key === 'invoice_quantity'
              ? wtRoundValue(rawValue)
              : wtFormatRoundedValue(rawValue, '', false, 2))
            : ''

        return (
          <AntTable.Summary.Cell
            key={column.key || column.dataIndex || index}
            index={index}
            style={{
              fontWeight: 700,
              backgroundColor: '#f5f7fa',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {content}
          </AntTable.Summary.Cell>
        )
      })}
    </AntTable.Summary.Row>
  )



  return (
    <div
      className="container-fluid"
      style={{
        padding: '0.75em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Form
        layout={'vertical'}
        form={form}
        onFinish={getReportData}
        style={{ maxWidth: '100%', margin: '0 auto' }}
      >
        <Card
          style={{ width: '100%' }}
          bodyStyle={{ padding: 16 }}
          title={
            <span style={{ fontSize: '26px', marginLeft: '40%' }}>
              Sale Book Report
            </span>
          }
        >
          <Row gutter={[16, 12]} align="bottom">
            <Col xs={24} sm={12} lg={6}>
              <label style={FILTER_LABEL_STYLE}>Date Range: *</label>
              <DatePicker.RangePicker
                onChange={(value) => {
                  setDateRange(value)
                  if (value && value.length === 2) {
                    setFromDate(value[0].format('YYYY-MM-DD'))
                    setToDate(value[1].format('YYYY-MM-DD'))
                  }
                }}
                style={COMPACT_FILTER_CONTROL_STYLE}
                value={dateRange}
              />
            </Col>

            <Col xs={24} sm={12} lg={4}>
              <label style={FILTER_LABEL_STYLE}>Status:</label>
              <WidgetSelector
                options={SALE_ORDER_INVOICES_STATUS_DROPDOWN_OPTIONS}
                selectedValue={statusId}
                displayValue={SALE_ORDER_INVOICES_STATUS_NAME[statusId]}
                onChange={(values) => changeValues("status", values)}
              />
            </Col>

            <Col xs={24} sm={12} lg={4}>
              <label style={FILTER_LABEL_STYLE}>Date Type:</label>
              <WidgetSelector
                options={SALE_ORDER_INVOICES_DATE_TYPE_DROPDOWN_OPTIONS}
                selectedValue={dateType}
                displayValue={SALE_ORDER_INVOICES_DATE_NAMES[dateType]}
                onChange={(values) => changeValues("date_type", values)}
              />
            </Col>

            <Col xs={24} sm={12} lg={4}>
              <label style={FILTER_LABEL_STYLE}>Items Level:</label>
              <Select
                value={itemLevel}
                style={COMPACT_FILTER_CONTROL_STYLE}
                options={ITEM_LEVEL_OPTIONS}
                onChange={(value) => changeValues('item_level', value)}
              />
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <label style={FILTER_LABEL_STYLE}>Branch:</label>
              <WidgetBranchSelector
                value={branchId}
                displayValue={branchName}
                branches={branches}
                placeholder="Select Branch"
                onChange={(values) => changeValues('branch_id', values)}
                showCloudIcon={false}
              />
            </Col>

          </Row>

          {searchMore && (
            <>
            <Row gutter={[16, 12]} align="bottom">
              <Col xs={24} sm={12} lg={6}>
                <label style={FILTER_LABEL_STYLE}>Customer</label>
                <WidgetCustomerSelector
                  onChange={(value) => changeValues('customer_id', value)}
                  customers={customers}
                  displayValue={PartyName}
                  value={PartyId}
                  reloadDropdownData={() => reloadParties()}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <label style={FILTER_LABEL_STYLE}>Project:</label>
                <WidgetProjectSelector
                  onChange={(value) => changeValues('project_id', value)}
                  data={projects} // <-- Use the formatted treeData here
                  value={ProjectId}
                  displayValue={ProjectName}
                  height={32}
                  reloadDropdownData={() => Base.dropdownData('projects', {}, [setProjects], 1)}
                />
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <label style={FILTER_LABEL_STYLE}>Department:</label>
                <WidgetDepartmentSelector
                  value={departmentId}
                  departments={departments}
                  onChange={(values) => changeValues('department_id', values)}
                  displayValue={departmentName}
                  reloadDropdownData={() =>
                    Base.dropdownData(
                      'departments',
                      { __branch_id: branchId, type: DEPARTMENT_TYPE_VIEW_SALE_ORDER },
                      [setDepartments],
                      1,
                    )
                  }
                />
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <label style={FILTER_LABEL_STYLE}>Revenue Account:</label>
                <Select
                  allowClear
                  showSearch
                  placeholder="Select Revenue Account"
                  value={RevenueAccountId || undefined}
                  style={FILTER_CONTROL_STYLE}
                  options={revenueAccounts}
                  optionFilterProp="label"
                  onChange={(value, option) => {
                    changeValues('revenue_account_id', value || 0)
                    setRevenueAccountName(option?.label || '')
                  }}
                />
              </Col>
            </Row>

            <Row gutter={[16, 12]} align="bottom">
              <Col xs={24} sm={12} lg={6}>
                <label style={FILTER_LABEL_STYLE}>FBR Status:</label>
                <Select
                  placeholder="Select FBR Status"
                  allowClear
                  value={fbrStatus}
                  style={COMPACT_FILTER_CONTROL_STYLE}
                  options={SALE_ORDER_INVOICES_FBR_STATUS_DROPDOWN_OPTIONS}
                  onChange={(values) => changeValues("fbr_status", values)}
                />
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <label style={FILTER_LABEL_STYLE}>FBR Inv No:</label>
                <CFormInput
                  placeholder="Select FBR Invoice No"
                  style={FILTER_CONTROL_STYLE}
                  value={fbrInvoiceNo}
                  onChange={(e) => changeValues("fbr_invoice_no", e.target.value)}
                />
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <label style={FILTER_LABEL_STYLE}>Party Type:</label>
                <Select
                  value={governmentClientType}
                  style={COMPACT_FILTER_CONTROL_STYLE}
                  options={CLIENT_TYPE_FILTER_OPTIONS}
                  onChange={(value) => changeValues('government_client_type', value)}
                />
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <label style={FILTER_LABEL_STYLE}>Scrap Sale:</label>
                <Select
                  value={scrapSaleType}
                  style={COMPACT_FILTER_CONTROL_STYLE}
                  options={SCRAP_SALE_FILTER_OPTIONS}
                  onChange={(value) => changeValues('scrap_sale_type', value)}
                />
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Button
                  style={{ marginTop: 12, marginRight: 5, height: 32, paddingInline: 12 }}
                  type="primary"
                  htmlType="submit"
                  disabled={isLoading ? true : false}
                >
                  Search
                </Button>
                <Button
                  style={{ marginTop: 12, marginRight: 5, backgroundColor: '#4BB543', color: '#fff', height: 32, paddingInline: 12 }}
                  onClick={exportToExcel}
                  disabled={isLoading ? true : false}
                >
                  Export
                </Button>
                <Button
                  style={{ marginTop: 12, marginRight: 5, color: '#fff', backgroundColor: '#E29C06', height: 32, paddingInline: 12 }}
                  onClick={() => setSearchMore(!searchMore)}
                  disabled={isLoading ? true : false}
                >
                  {searchMore ? 'Less...' : 'More...'}
                </Button>
              </Col>
            </Row>
            </>
          )}

          {!searchMore && (
            <Row gutter={[16, 12]}>
              <Col xs={24}>
                <Button
                  style={{ marginTop: 12, marginRight: 5, height: 32, paddingInline: 12 }}
                  type="primary"
                  htmlType="submit"
                  disabled={isLoading ? true : false}
                >
                  Search
                </Button>
                <Button
                  style={{ marginTop: 12, marginRight: 5, backgroundColor: '#4BB543', color: '#fff', height: 32, paddingInline: 12 }}
                  onClick={exportToExcel}
                  disabled={isLoading ? true : false}
                >
                  Export
                </Button>
                <Button
                  style={{ marginTop: 12, marginRight: 5, color: '#fff', backgroundColor: '#E29C06', height: 32, paddingInline: 12 }}
                  onClick={() => setSearchMore(!searchMore)}
                  disabled={isLoading ? true : false}
                >
                  {searchMore ? 'Less...' : 'More...'}
                </Button>
              </Col>
            </Row>
          )}
        </Card>
      </Form>


      <Card style={{ marginTop: 30 }}>
        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={saleBookReportData}
            pagination={false}
            bordered
            size="small"
            tableLayout="auto"
            scroll={{ x: itemLevel === 1 ? 2800 : 2500, y: 400 }}
            virtual={true}
            summary={renderTableSummary}
            loading={{ indicator: <div><Spin /></div>, spinning: isLoading }}
          />
        </Spin>
      </Card>
    </div>
  )
}
export default SaleBookReport
